import { ScrollText, Info } from 'lucide-react'
import { DocSection } from '@/components/docs/doc-section'
import { DocPageHeader } from '@/components/docs/doc-page-header'
import { DocNav } from '@/components/docs/doc-nav'
import { MermaidDiagram } from '@/components/docs/mermaid-diagram'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'

export function PoliciesPage() {
  return (
    <>
      <DocPageHeader icon={ScrollText} title="Policies" />

      <div className="flex flex-1 flex-col gap-6 overflow-auto p-6 max-w-4xl">
        <DocSection title="What is a Policy?">
          <p className="text-sm text-muted-foreground leading-relaxed">
            A policy is a set of rules that evaluates attributes to determine whether to permit or
            deny an access request. Policies are the core building blocks of ABAC — they encode your
            organization's access control requirements in a structured, enforceable format. Rather
            than hardcoding permissions into application logic, you express them as declarative
            policies that the PDP can evaluate at runtime.
          </p>
        </DocSection>

        <DocSection title="Policy Structure">
          <MermaidDiagram
            src="/docs/diagrams/policy-structure.svg"
            alt="Policy structure showing Target, Rules, and Combining Algorithm"
            caption="Anatomy of an XACML policy"
          />
        </DocSection>

        <DocSection title="Policy Components">
          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Target</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Determines which requests this policy applies to. A target matches on subject,
                  resource, and action attributes. If a request doesn't match the target, the policy
                  is skipped entirely. Think of it as a filter: only requests that pass through the
                  target reach the rules inside.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Rules</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Each rule has an Effect (
                  <Badge variant="default" className="bg-green-600 text-white mx-0.5">
                    Permit
                  </Badge>{' '}
                  or{' '}
                  <Badge variant="default" className="bg-red-600 text-white mx-0.5">
                    Deny
                  </Badge>
                  ) and a Condition — an attribute expression that must evaluate to true for the rule
                  to fire. Rules are the decision-making units within a policy. A single policy can
                  contain multiple rules, each addressing a different scenario.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Combining Algorithm</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  When multiple rules produce different results, the combining algorithm resolves the
                  conflict. Common algorithms include{' '}
                  <strong className="text-foreground">deny-overrides</strong> (security-first: if any
                  rule says Deny, the whole policy returns Deny) and{' '}
                  <strong className="text-foreground">permit-overrides</strong> (convenience-first:
                  if any rule says Permit, the policy returns Permit). Choosing the right algorithm
                  depends on whether you want to default toward restriction or access.
                </p>
              </CardContent>
            </Card>
          </div>
        </DocSection>

        <DocSection title="How Attributes Appear in Policies">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Example condition</AlertTitle>
            <AlertDescription>
              <pre className="mt-2 rounded bg-muted p-3 text-xs font-mono leading-relaxed">
{`subject.department == "finance"
AND resource.sensitivity IN ["high", "critical"]
AND action.id == "SELECT"`}
              </pre>
            </AlertDescription>
          </Alert>
          <p className="text-sm text-muted-foreground leading-relaxed">
            This condition permits only finance department users to run SELECT queries on high or
            critical sensitivity tables. The PDP evaluates these expressions against the attributes
            in each request. Every attribute reference — <code className="text-xs bg-muted px-1 py-0.5 rounded">subject.department</code>,{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">resource.sensitivity</code>,{' '}
            <code className="text-xs bg-muted px-1 py-0.5 rounded">action.id</code> — maps to a
            real attribute definition in your system. When you design policies in poli-x, the platform
            validates that every attribute you reference actually exists.
          </p>
        </DocSection>

        <DocNav
          prev={{ title: 'Attributes', url: '/docs/attributes' }}
          next={{ title: 'Request Evaluation', url: '/docs/evaluation' }}
        />
      </div>
    </>
  )
}
